import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../language/LanguageProvider.jsx';
import { getSeriesInfo, getVodInfo } from '../../other/load-playlist';
import { optimizeName } from '../../other/vod-series-name-optimizer';
import { useYoutubeErrorDetector } from '../../other/youtube-error-detector';
import { media } from '../../other/breakpoints';

const Container = styled.div`
  height: calc(70vh - 2rem);
  position: relative;
  z-index: 1000;
  overflow: hidden;
  margin-bottom: 2rem;

  ${media.tablet} {
    margin-bottom: 1.5rem;
  }

  ${media.mobile} {
    margin-bottom: 1rem;
  }
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -2;
  width: 100vw;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: blur(0.3rem);
`;

const VideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  overflow: hidden;
  z-index: -1;
  background-color: var(--first-color);

  & > div {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background-color: var(--first-color);
  }

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  padding: 1.2rem 2rem 2rem 2rem;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0.45) 45%,
    rgba(0, 0, 0, 0.35) 60%,
    rgba(0, 0, 0, 0.15) 80%,
    rgba(0, 0, 0, 0) 100%
  );

  ${media.tablet} {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }

  ${media.mobile} {
    padding: 0.75rem 1rem 1rem 1rem;
  }
`;

const ContentWrap = styled.div`
  max-width: 70ch;
  padding-right: 1rem;

  ${media.tablet} {
    max-width: 65ch;
    padding-right: 0.75rem;
  }

  ${media.mobile} {
    max-width: 100%;
    padding-right: 0.5rem;
  }
`;

const TitleH1 = styled.h1`
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  margin-bottom: 1rem;

  ${media.mobile} {
    margin-bottom: 0.75rem;
  }
`;

const TitleH3 = styled.h3`
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  margin-bottom: 1rem;

  ${media.mobile} {
    margin-bottom: 0.75rem;
  }
`;

const FallbackGradient = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;
  background: linear-gradient(
    135deg,
    var(--first-color) 0%,
    rgba(20, 21, 26, 0.95) 100%
  );
`;

const PosterContentWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2rem;
  width: 100%;

  ${media.tablet} {
    gap: 1.5rem;
  }

  ${media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const PosterWrapper = styled.div`
  flex-shrink: 0;
  max-width: 200px;
  width: 100%;

  ${media.tablet} {
    max-width: 150px;
  }

  ${media.mobile} {
    max-width: 120px;
  }
`;

const PosterImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  aspect-ratio: 2 / 3;
  object-fit: cover;
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  ${media.mobile} {
    gap: 0.5rem;
  }
`;

const Desc = styled.label`
  -webkit-text-stroke-width: 0px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  margin-bottom: 0.5rem;

  ${media.mobile} {
    -webkit-line-clamp: 2;
    margin-bottom: 0.25rem;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  ${media.tablet} {
    gap: 0.75rem;
  }

  ${media.mobile} {
    gap: 0.5rem;
    flex-direction: column;
  }
`;

const ButtonMain = styled(Link)`
  color: black !important;
  -webkit-text-fill-color: black;
  -webkit-text-stroke-width: 0px;
  padding: 0.6rem 1.5rem;
  transition: all 0.3s ease;

  ${media.tablet} {
    padding: 0.5rem 1.25rem;
  }

  ${media.mobile} {
    padding: 0.5rem 1rem;
    width: 100%;
    text-align: center;
  }
`;

const ButtonSecond = styled(Link)`
  color: white !important;
  -webkit-text-fill-color: white;
  -webkit-text-stroke-width: 0px;
  padding: 0.6rem 1.5rem;
  transition: all 0.3s ease;

  ${media.tablet} {
    padding: 0.5rem 1.25rem;
  }

  ${media.mobile} {
    padding: 0.5rem 1rem;
    width: 100%;
    text-align: center;
  }
`;

const Shadow = styled.div`
  position: absolute;
  width: 100vw;
  height: 23vh;
  bottom: 0;
  left: 0;
  z-index: 0;
  background-image: linear-gradient(
    180deg,
    transparent,
    rgba(37, 37, 37, 0.61),
    var(--first-color)
  );
`;

const BackgroundPlayer = ({
  name,
  stream_icon,
  stream_id,
  category_id,
  isSeries,
  style,
  title,
  existingTmdb,
}) => {
  const [Name, setName] = useState(optimizeName(name));
  const [ImageSrc, setImageSrc] = useState(stream_icon);
  const [Description, setDescription] = useState('');
  const [youtubeId, setYoutubeId] = useState(null);
  const [backdropPath, setBackdropPath] = useState(null);
  const iframeRef = useRef(null);

  const { status: youtubeStatus, registerIframe } =
    useYoutubeErrorDetector(youtubeId);

  useEffect(() => {
    async function fetchData() {
      (isSeries
        ? getSeriesInfo(stream_id, name, true, existingTmdb)
        : getVodInfo(stream_id, name, existingTmdb)
      ).then((result) => {
        if (result && result.info) {
          result = result.info;
          result.name && setName(result.name);
          result.image && setImageSrc(result.image);
          result.cover && setImageSrc(result.cover);
          result.description && setDescription(result.description);
          result.youtube_trailer && setYoutubeId(result.youtube_trailer);
          result.backdrop_path &&
            result.backdrop_path.length > 0 &&
            setBackdropPath(result.backdrop_path[0]);
        }
      });
    }
    fetchData();
  }, [stream_id, isSeries, name, existingTmdb]);

  const { t } = useLang();

  const hasBackdrop = backdropPath && backdropPath.length > 0;
  const youtubeError = youtubeStatus === 'error';

  const shouldShowYoutube = youtubeId && !youtubeError;
  const shouldShowBackdrop = youtubeError || !youtubeId;

  useEffect(() => {
    if (iframeRef.current) {
      registerIframe(iframeRef.current);
    }
  }, [registerIframe, youtubeId]);

  return (
    <Container className="parent" style={style}>
      {shouldShowYoutube && (
        <VideoContainer
          style={{
            opacity: youtubeError ? 0 : 1,
            transition: 'opacity 0.5s ease',
          }}>
          <div>
            <iframe
              ref={iframeRef}
              frameBorder="0"
              height="100%"
              width="100%"
              title={t('vod.youtubeBgTitle')}
              src={`https://youtube.com/embed/${youtubeId}?autoplay=1&controls=0&showinfo=0&autohide=1&mute=1&loop=1&disablekb=1&modestbranding=1&playlist=${youtubeId}&origin=${window.location.origin}`}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </VideoContainer>
      )}
      {shouldShowBackdrop && hasBackdrop && (
        <BackgroundImage
          style={{
            background: `url("${backdropPath}") no-repeat center center`,
            backgroundSize: 'cover',
          }}
        />
      )}
      {shouldShowBackdrop && !hasBackdrop && <FallbackGradient />}
      <Overlay>
        <ContentWrap>
          <TitleH1>
            <b>
              {title
                ? title
                : isSeries
                ? t('vod.titleSeries')
                : t('vod.titleMovies')}
            </b>
          </TitleH1>
          <PosterContentWrapper>
            <PosterWrapper>
              <PosterImage src={ImageSrc} loading="lazy" alt={Name} />
            </PosterWrapper>
            <ContentSection>
              <TitleH3>
                <b>{Name}</b>
              </TitleH3>
              <Desc>{Description}</Desc>
              <ButtonsContainer>
                {isSeries !== true && (
                  <ButtonMain
                    type="button"
                    id="btn-1"
                    className="btn btn-light vod"
                    to={`/movie/category/${category_id}/${stream_id}/play/`}
                    aria-label={t('vod.play')}>
                    <i className="fas fa-play mr-2"></i>
                    {t('vod.play')}
                  </ButtonMain>
                )}
                <ButtonSecond
                  type="button"
                  id="btn-2"
                  className="btn btn-secondary vod"
                  to={`/${
                    isSeries ? 'series' : 'movie'
                  }/category/${category_id}/${stream_id}/info/`}
                  aria-label={t('vod.moreInfo')}>
                  <i className="fas fa-info-circle mr-2"></i>
                  {t('vod.moreInfo')}
                </ButtonSecond>
              </ButtonsContainer>
            </ContentSection>
          </PosterContentWrapper>
        </ContentWrap>
      </Overlay>
      <Shadow />
    </Container>
  );
};

export default BackgroundPlayer;
